import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { viewTransition } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo, getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/** Paginates the existing grid, preparing the next batch before it is visible. */
export default class PaginatedList extends Component {
  pages = new Map();
  infinityScrollObserver;
  #preparedPages = new Map();
  #pendingPages = new Map();
  #rendering = new Set();
  #generation = 0;
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    this.#prepareAdjacentPages();
    this.#observeViewMore();
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    scrollContainerMediaQuery.addEventListener('change', this.#handleScrollContainerChange);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#resetPages();
    this.#prepareAdjacentPages();
    this.#observeViewMore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.infinityScrollObserver?.disconnect();
    this.#resetPages();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleScrollContainerChange);
  }

  #resetPages() {
    this.#generation++;
    this.pages.clear();
    this.#preparedPages.clear();
    this.#pendingPages.clear();
    this.#rendering.clear();
  }

  #observeViewMore() {
    this.infinityScrollObserver?.disconnect();
    if (this.getAttribute('infinite-scroll') === 'false') return;
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(async (entries) => {
        if (viewTransition.current) await viewTransition.current;
        if (!this.isConnected) return;
        for (const entry of entries) {
          if (!entry.isIntersecting || !this.contains(entry.target)) continue;
          if (entry.target === this.refs.viewMorePrevious) this.#renderPage('previous');
          if (entry.target === this.refs.viewMoreNext) this.#renderPage('next');
        }
      }, { root: getIntersectionRoot(), rootMargin: '100px 0px 1200px 0px' });
    }
    for (const target of [this.refs.viewMorePrevious, this.refs.viewMoreNext]) {
      if (target) this.infinityScrollObserver.observe(target);
    }
  }

  #shouldUsePage(pageInfo) {
    const lastPage = Number(this.refs.grid?.dataset.lastPage);
    return !!pageInfo && pageInfo.page >= 1 && pageInfo.page <= lastPage;
  }

  #handleScrollContainerChange = () => {
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;
    this.#observeViewMore();
  };

  #prepareAdjacentPages() {
    if (this.getAttribute('infinite-scroll') === 'false') return;
    for (const direction of ['next', 'previous']) {
      const page = this.#getPage(direction);
      if (this.#shouldUsePage(page)) {
        // A failed speculative request is retried when the shopper reaches it.
        this.#fetchSpecificPage(page).catch(() => {});
      }
    }
  }

  async #fetchSpecificPage(pageInfo) {
    if (!this.#shouldUsePage(pageInfo)) return;
    const { page, url } = pageInfo;
    if (this.#preparedPages.has(page)) return this.#preparedPages.get(page);
    const key = url.toString();
    if (this.#pendingPages.has(key)) return this.#pendingPages.get(key);
    const generation = this.#generation;
    const pending = (async () => {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, new URL(url));
      if (!this.isConnected || generation !== this.#generation) return;
      // Parsing is done during prefetch, rather than at the bottom of the list.
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const grid = parsed.querySelector('[ref="grid"]');
      const cards = grid ? Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')) : [];
      if (!cards.length) throw new Error('The next product page could not be loaded.');
      this.pages.set(page, html);
      this.#preparedPages.set(page, cards);
      return cards;
    })();
    this.#pendingPages.set(key, pending);
    try { return await pending; }
    finally { if (this.#pendingPages.get(key) === pending) this.#pendingPages.delete(key); }
  }

  async #renderPage(direction) {
    const grid = this.refs.grid;
    const page = this.#getPage(direction);
    if (!grid || !this.#shouldUsePage(page) || this.#rendering.has(direction)) return;
    this.#rendering.add(direction);
    const generation = this.#generation;
    try {
      const cards = await this.#fetchSpecificPage(page);
      if (!cards || !this.isConnected || generation !== this.#generation || grid !== this.refs.grid) return;
      if (this.#getPage(direction)?.page !== page.page) return;
      const previous = direction === 'previous';
      const first = grid.firstElementChild;
      const currentScroll = getScrollTop();
      const oldTop = previous && first ? first.getBoundingClientRect().top : 0;
      // One insertion means one custom-element connection batch and observer update.
      const fragment = document.createDocumentFragment();
      fragment.append(...cards);
      previous ? grid.prepend(fragment) : grid.append(fragment);
      this.#aspectRatioHelper?.processNewElements();
      if (previous && first) {
        scrollTo({ top: currentScroll + first.getBoundingClientRect().top - oldTop, behavior: 'instant' });
      }
      const sentinel = previous ? this.refs.viewMorePrevious : this.refs.viewMoreNext;
      sentinel?.querySelector('[data-page-retry]')?.remove();
      history.pushState('', '', page.url.toString());
      this.pages.delete(page.page);
      this.#preparedPages.delete(page.page);
      // Start the next request immediately. An idle callback can starve during scrolling.
      this.#prepareAdjacentPages();
      requestAnimationFrame(() => {
        if (this.isConnected && generation === this.#generation) this.#observeViewMore();
      });
    } catch (error) {
      if (generation === this.#generation && this.isConnected) this.#showRetry(direction);
      console.warn('Product pagination request failed:', error);
    } finally {
      if (generation === this.#generation) this.#rendering.delete(direction);
    }
  }

  #showRetry(direction) {
    const target = direction === 'previous' ? this.refs.viewMorePrevious : this.refs.viewMoreNext;
    if (!target || target.querySelector('[data-page-retry]')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-secondary';
    button.dataset.pageRetry = '';
    button.textContent = 'Ürünleri yüklemeyi tekrar dene';
    button.addEventListener('click', () => this.#renderPage(direction));
    target.append(button);
  }

  #getPage(direction) {
    // Read the current boundary directly: Component refs update asynchronously.
    const grid = this.refs.grid;
    const previous = direction === 'previous';
    const card = previous ? grid?.querySelector(':scope > [ref="cards[]"]') : grid?.lastElementChild;
    const currentPage = Number(card?.dataset.page);
    if (!currentPage) return;
    const page = currentPage + (previous ? -1 : 1);
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return { page, url };
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #handleFilterUpdate = (event) => {
    const section = event.target?.closest?.('[id^="shopify-section-"]');
    if (section && section.id !== `shopify-section-${this.sectionId}`) return;
    this.#resetPages();
    // Morph invokes updatedCallback too. This also covers unchanged page counts.
    const generation = this.#generation;
    requestAnimationFrame(() => {
      if (this.isConnected && generation === this.#generation) {
        this.#prepareAdjacentPages();
        this.#observeViewMore();
      }
    });
  };
}

